// Admin → Failures (2026-10-22, migration 0154): what each automation_failures
// row is, in words. A real failure, a partial ("partial: <what> not done",
// something that changed had already succeeded) and a run that completed by
// its fallback each get their own label, so a fallback is never read as a
// failure. Pure, for the page and its unit test.

export const OUTCOME_COLORS = Object.freeze({ failure: 'error', partial: 'warning', fallback: 'info' })

// The page's two filters (2026-10-29): "Failures" (a failure or a partial,
// the rows the top-nav count counts) and "Completed another way" (a run that
// finished by its fallback, e.g. a return label sent by message because eBay
// refused to attach it): listed, never mixed in with the failures and never
// counted.
export const FAILURE_FILTERS = Object.freeze([
  { value: 'failures', label: 'Failures' },
  { value: 'completed_another_way', label: 'Completed another way' },
])

export function isCompletedAnotherWay(failure) {
  return (failure?.outcome ?? 'failure') === 'fallback'
}

export function filterFailures(failures, filter = 'failures') {
  const list = Array.isArray(failures) ? failures : []
  return filter === 'completed_another_way' ? list.filter(isCompletedAnotherWay) : list.filter((f) => !isCompletedAnotherWay(f))
}

export function failureFilterCounts(failures) {
  const list = Array.isArray(failures) ? failures : []
  const other = list.filter(isCompletedAnotherWay).length
  return { failures: list.length - other, completed_another_way: other }
}

// The "<what>" of a partial, from the message the server wrote
// ("partial: <what> — <error>").
export function partialWhat(failureMessage) {
  const m = /^partial: (.+?) — /.exec(String(failureMessage ?? ''))
  return m ? m[1] : null
}

export function outcomeLabel(failure) {
  const outcome = failure?.outcome ?? 'failure'
  if (outcome === 'fallback') return 'Completed with fallback'
  if (outcome === 'partial') {
    const what = partialWhat(failure?.failureMessage)
    return what ? `Partial: ${what}` : 'Partial'
  }
  return 'Failure'
}

// "Status 403 · eBay" style summary of the saved error, or null.
export function errorSummary(errorDetail) {
  if (!errorDetail) return null
  const parts = []
  if (errorDetail.status != null) parts.push(`Status ${errorDetail.status}`)
  if (errorDetail.service) parts.push(errorDetail.service)
  // The API's own code and message (2026-10-27, every integration):
  // "Status 400 · TRQ · ERR-TRQ114: Return quantity exceeded ...", so the
  // page says what the API refused.
  if (errorDetail.code || errorDetail.providerMessage) parts.push([errorDetail.code, errorDetail.providerMessage].filter(Boolean).join(': '))
  return parts.length ? parts.join(' · ') : null
}

// The Activity Log's error chip for a refused call (2026-10-27,
// server/src/integrations/api-error-message.js): "eBay refused this:
// <message> (error 34916)" when the API gave a message, else today's
// "error: <status text>". null for a call that did not fail. errorSystem
// is stored with the message; a row from before it (TRQ only) is TRQ. TRQ
// and the storefront EDD API show as "Console" (2026-10-28), a stored
// "TRQ" / "Storefront" too.
const SYSTEM_DISPLAY_NAMES = { TRQ: 'Console', Storefront: 'Console' }
export function callErrorText(call) {
  if (!call?.error) return null
  if (!call.errorMessage) return `error: ${call.error}`
  const code = call.errorCode == null || call.errorCode === '' ? '' : ` (${/^\d+$/.test(String(call.errorCode)) ? `error ${call.errorCode}` : call.errorCode})`
  const system = call.errorSystem ?? 'TRQ'
  // errorHint (2026-10-29): the text the executor worded for this refusal
  // (eBay's 34916: what is open on the order), when it set one.
  const hint = typeof call.errorHint === 'string' && call.errorHint ? ` ${call.errorHint}` : ''
  return `${SYSTEM_DISPLAY_NAMES[system] ?? system} refused this: ${call.errorMessage}${code}${hint}`
}

// The saved response body as readable text (already redacted server-side).
export function errorBodyText(errorDetail) {
  const body = errorDetail?.body
  if (body == null) return null
  if (typeof body === 'string') return body
  try { return JSON.stringify(body, null, 2) } catch { return String(body) }
}

export function succeededStepsText(steps) {
  const list = Array.isArray(steps) ? steps : []
  return list.map((s) => s?.label || s?.type).filter(Boolean)
}
